// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import * as faceapi from "face-api.js";
import Logger from "js-logger";
import { ChallengeDetails } from "@/js/RemoteStarter";
import { Drawer, DrawOptions } from "@/js/Drawer";
import { Utils } from "@/js/Utils";

export interface StateOutput {
  readonly nextState?: State;
  readonly drawOptions?: DrawOptions;
  readonly helpMessage?: string;
  readonly helpAnimationNumber?: number;
}

export abstract class State {
  constructor(readonly challengeDetails: ChallengeDetails) {}

  process(
    // eslint-disable-next-line
    _faces: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateOutput {
    return {};
  }

  getMaximumDurationInSeconds(): number {
    return -1;
  }

  protected isFaceBoxInsideFaceArea(faceBox: faceapi.Box, addTolerance = true) {
    const tolerance: number = addTolerance ? parseInt(Utils.getConfig().FACE_AREA_TOLERANCE_PERCENT) / 100 : 0;
    const toleranceX: number = tolerance * this.challengeDetails.areaWidth;
    const toleranceY: number = tolerance * this.challengeDetails.areaHeight;
    return (
      faceBox.x >= this.challengeDetails.areaLeft - toleranceX &&
      faceBox.y >= this.challengeDetails.areaTop - toleranceY &&
      faceBox.x + faceBox.width <= this.challengeDetails.areaLeft + this.challengeDetails.areaWidth + toleranceX &&
      faceBox.y + faceBox.height <= this.challengeDetails.areaTop + this.challengeDetails.areaHeight + toleranceY
    );
  }

  protected isNoseInsideNoseArea(nose: faceapi.IPoint) {
    return (
      nose.x >= this.challengeDetails.noseLeft &&
      nose.y >= this.challengeDetails.noseTop &&
      nose.x <= this.challengeDetails.noseLeft + this.challengeDetails.noseWidth &&
      nose.y <= this.challengeDetails.noseTop + this.challengeDetails.noseHeight
    );
  }

  protected isFaceTooLarge(faceBox: faceapi.Box) {
    return faceBox.width > this.challengeDetails.areaWidth;
  }

  protected get challengeType(): string {
    return this.challengeDetails.challengeType;
  }

  abstract getName(): string;
}

export class FailState extends State {
  static NAME = "FailState";

  getName(): string {
    return FailState.NAME;
  }
}

export class SuccessState extends State {
  static NAME = "SuccessState";

  getName(): string {
    return SuccessState.NAME;
  }
}

export class NoseState extends State {
  static NAME = "NoseState";

  private framesWithoutFace = 0;
  private landmarkIndex = parseInt(Utils.getConfig().LANDMARK_INDEX);

  process(
    faces: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateOutput {
    let nextState: State = this;
    if (faces.length === 1) {
      if (this.isFaceBoxInsideFaceArea(faces[0].detection.box)) {
        if (this.isNoseInsideNoseArea(faces[0].landmarks.positions[this.landmarkIndex])) {
          nextState = new SuccessState(this.challengeDetails);
        }
        this.framesWithoutFace = 0;
      } else {
        Logger.warn(`NoseState fail: isFaceBoxInsideFaceArea=${this.isFaceBoxInsideFaceArea(faces[0].detection.box)}`);
        nextState = new FailState(this.challengeDetails);
      }
    } else {
      if (
        faces.length !== 0 ||
        ++this.framesWithoutFace > parseInt(Utils.getConfig().STATE_NOSE_MAX_FRAMES_WITHOUT_FACE)
      ) {
        Logger.warn(`NoseState fail: #faces=${faces.length} framesWithoutFace=${this.framesWithoutFace}`);
        nextState = new FailState(this.challengeDetails);
      } else {
        Logger.warn(`no face detected. Skipping frame...`);
      }
    }
    const drawOptions: DrawOptions = {
      faceDrawBoxOptions: {
        boxColor: Drawer.COLOR_GREEN
      },
      noseDrawBoxOptions: {
        boxColor: Drawer.COLOR_YELLOW
      }
    };
    return {
      nextState: nextState,
      drawOptions: drawOptions,
      helpMessage: "challenge.messages.moveNoseRequestMessage",
      helpAnimationNumber: 2
    };
  }

  getMaximumDurationInSeconds(): number {
    return parseInt(Utils.getConfig().STATE_NOSE_DURATION_IN_SECONDS);
  }

  getName(): string {
    return NoseState.NAME;
  }
}

export class FaceUpState extends State {
  static NAME = "FaceUpState";
  private framesWithoutFace = 0;
  private noseIndex = parseInt(Utils.getConfig().LANDMARK_INDEX);
  private hasFaceDown = false;
  private hasFaceUp = false;
  private isFaceDown(face: faceapi.FaceLandmarks68): boolean {
    const leftPoint = face.positions[0];
    const rightPoint = face.positions[16];
    const nosePoint = face.positions[this.noseIndex];
    const { x: leftX, y: leftY } = leftPoint;
    const { x: rightX, y: rightY } = rightPoint;
    const { x: noseX, y: noseY } = nosePoint;
    return noseX < rightX && noseX > leftX && noseY < leftY && noseY < rightY;
  }
  private isFaceUp(face: faceapi.FaceLandmarks68): boolean {
    const leftPoint = face.positions[0];
    const rightPoint = face.positions[16];
    const nosePoint = face.positions[this.noseIndex];
    const { x: leftX, y: leftY } = leftPoint;
    const { x: rightX, y: rightY } = rightPoint;
    const { x: noseX, y: noseY } = nosePoint;
    return noseX < rightX && noseX > leftX && noseY > leftY && noseY > rightY;
  }

  process(
    faces: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateOutput {
    let nextState: State = this;
    if (faces.length === 1) {
      if (this.isFaceBoxInsideFaceArea(faces[0].detection.box)) {
        if (this.isFaceUp(faces[0].landmarks)) {
          this.hasFaceUp = true;
        }
        if (this.isFaceDown(faces[0].landmarks)) {
          this.hasFaceDown = true;
        }
        if (this.hasFaceDown && this.hasFaceUp) {
          nextState = new SuccessState(this.challengeDetails);
        }
        this.framesWithoutFace = 0;
      } else {
        Logger.warn(
          `FaceUpState fail: isFaceBoxInsideFaceArea=${this.isFaceBoxInsideFaceArea(faces[0].detection.box)}`
        );
        nextState = new FailState(this.challengeDetails);
      }
    } else {
      if (
        faces.length !== 0 ||
        ++this.framesWithoutFace > parseInt(Utils.getConfig().STATE_FACE_UP_MAX_FRAMES_WITHOUT_FACE)
      ) {
        Logger.warn(`FaceUpState fail: #faces=${faces.length} framesWithoutFace=${this.framesWithoutFace}`);
        nextState = new FailState(this.challengeDetails);
      } else {
        Logger.warn(`no face detected. Skipping frame...`);
      }
    }
    const drawOptions: DrawOptions = {
      faceDrawBoxOptions: {
        boxColor: Drawer.COLOR_GREEN
      }
    };
    return {
      nextState: nextState,
      drawOptions: drawOptions,
      helpMessage: "challenge.messages.faceUpRequestMessage",
      helpAnimationNumber: 3
    };
  }

  getMaximumDurationInSeconds(): number {
    return parseInt(Utils.getConfig().STATE_NOSE_DURATION_IN_SECONDS);
  }

  getName(): string {
    return FaceUpState.NAME;
  }
}

export class AreaState extends State {
  static NAME = "AreaState";

  private framesWithoutFace = 0;
  private framesWithFace = 0;
  private framesWithFaceTooLarge = 0;
  private lastIsFaceTooLarge = false;
  private posibleFailedReason() {
    const fps = parseInt(Utils.getConfig().MAX_FPS);
    if (this.framesWithFace < 5 * fps) {
      return "blurred";
    }
    return "no move";
  }

  process(
    faces: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateOutput {
    let nextState: State = this;
    let boxColor = Drawer.COLOR_RED;
    if (faces.length === 1) {
      if (
        this.framesWithFaceTooLarge < parseInt(Utils.getConfig().STATE_AREA_MAX_FRAMES_REQUEST_FOR_FACE_TOO_LARGE) &&
        this.isFaceTooLarge(faces[0].detection.box)
      ) {
        this.lastIsFaceTooLarge = true;
        this.framesWithFaceTooLarge++;
      } else {
        this.lastIsFaceTooLarge = false;
        this.framesWithFaceTooLarge = 0;
      }

      if (!this.lastIsFaceTooLarge && this.isFaceBoxInsideFaceArea(faces[0].detection.box)) {
        boxColor = Drawer.COLOR_GREEN;
        nextState = this.getNextState();
      }
      this.framesWithoutFace = 0;
      this.framesWithFace++;
    } else {
      if (
        faces.length !== 0 ||
        ++this.framesWithoutFace > parseInt(Utils.getConfig().STATE_AREA_MAX_FRAMES_WITHOUT_FACE)
      ) {
        Logger.warn(`AreaState fail: ${this.posibleFailedReason()}`);
        nextState = new FailState(this.challengeDetails);
      } else {
        Logger.warn(`no face detected. Skipping frame...`);
      }
    }
    const drawOptions: DrawOptions = {
      faceDrawBoxOptions: {
        boxColor: boxColor
      }
    };
    const helpMessage = this.lastIsFaceTooLarge
      ? "challenge.messages.faceTooLarge"
      : "challenge.messages.faceIsNotInCenter";
    return {
      nextState: nextState,
      drawOptions: drawOptions,
      helpMessage,
      helpAnimationNumber: 1
    };
  }

  getMaximumDurationInSeconds(): number {
    return parseInt(Utils.getConfig().STATE_AREA_DURATION_IN_SECONDS);
  }

  getName(): string {
    return AreaState.NAME;
  }
  private getNextState(): State {
    if (this.challengeType === "faceup") {
      return new FaceUpState(this.challengeDetails);
    }
    return new NoseState(this.challengeDetails);
  }
}

export class FaceState extends State {
  static NAME = "FaceState";

  process(
    faces: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateOutput {
    let nextState: State = this;
    let helpMessage = undefined;
    switch (faces.length) {
      case 0:
        helpMessage = "challenge.messages.noFaceDetected";
        break;
      case 1:
        nextState = new AreaState(this.challengeDetails);
        break;
      default:
        Logger.warn("FaceState fail. More than one face detected");
        helpMessage = "challenge.messages.tooManyFacesDetected";
    }
    const drawOptions: DrawOptions = {
      faceDrawBoxOptions: {
        boxColor: Drawer.COLOR_RED
      }
    };
    return {
      nextState: nextState,
      drawOptions: drawOptions,
      helpMessage: helpMessage
    };
  }

  getName(): string {
    return FaceState.NAME;
  }
}
